import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  getAvailableGuestGroupsByEvent,
  submitGuestGroupClaimAction,
  getDasDeviceDetails
} from "../../features/das-device-manager/dasDeviceManagerSlice";
import { usePromoTracking } from "../../hooks/usePromoTracking";
import packageJson from "../../../package.json";

import ProgramSchedule from "./Schedule";
import HotelCard from "./HotelCard";
import Balance from "./Balance";
import Memories from "./Memories";
import PassPreview from "./PassPreview";
import ClaimGroupModal from "./ClaimGroupModal";
import { IMAGES } from "./images";
import {
  MainContainer,
  NavBar,
  Hero,
  Chip,
  Section,
  TileGrid,
  Tile,
  Footer,
  MissingParams,
  PageStatus
} from "./styles";
import copyIconLight from "./imgs/copy-icon-light.svg";

const getHotelName = (deviceDetails) =>
  deviceDetails?.attendance?.customFields?.hotel?.value ||
  deviceDetails?.group?.customFields?.hotelName?.value ||
  "";

const DemoEvent = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const eventId = searchParams.get("eventId");
  const hasRequiredParams = Boolean(serialNumber && eventId);

  const [copied, setCopied] = useState(false);
  const [isBalanceOpen, setIsBalanceOpen] = useState(false);
  const [isMemoriesOpen, setIsMemoriesOpen] = useState(false);
  const [isPassOpen, setIsPassOpen] = useState(false);
  const [isClaimGroupModalOpen, setIsClaimGroupModalOpen] = useState(false);

  const dispatch = useDispatch();
  const {
    deviceDetails,
    gettingDeviceDetails,
    errorGettingDeviceDetails,
    availableGuestGroups,
    gettingAvailableGuestGroups,
    errorGettingAvailableGuestGroups,
    claimGuestGroupSubmitting,
    claimGuestGroupSubmitSuccess,
    claimGuestGroupSubmitError
  } = useSelector((state) => state.dasDevices);

  const trackingEventId = deviceDetails?.attendance?.eventId || eventId;
  const trackingGuestId = deviceDetails?.attendance?.guestId;
  const { trackButtonClick, trackUserAction } = usePromoTracking({
    eventId: trackingEventId,
    guestId: trackingGuestId,
    serialNumber,
    pageTitle: "Riverside Sessions"
  });
  const hasTrackedPageEnd = useRef(false);
  const latestTrackUserAction = useRef(() => {});

  const trackDemoButtonClick = useCallback(
    (buttonName, location) => {
      trackButtonClick({
        buttonName,
        location
      });
    },
    [trackButtonClick]
  );

  const trackDemoUserAction = useCallback(
    (userAction, location = "demo_event_page") => {
      trackUserAction({
        userAction,
        location
      });
    },
    [trackUserAction]
  );

  useEffect(() => {
    if (!hasRequiredParams) return;
    dispatch(
      getDasDeviceDetails({
        serial: serialNumber,
        eventId,
        locale: "en"
      })
    );
  }, [serialNumber, eventId, hasRequiredParams, dispatch]);

  useEffect(() => {
    latestTrackUserAction.current = trackDemoUserAction;
  }, [trackDemoUserAction]);

  useEffect(() => {
    if (!copied) return undefined;
    const timeoutId = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  const shouldShowClaimModal =
    hasRequiredParams &&
    Boolean(!deviceDetails?.attendance) &&
    !claimGuestGroupSubmitSuccess;

  useEffect(() => {
    if (!shouldShowClaimModal || !eventId || isClaimGroupModalOpen) {
      return;
    }

    setIsClaimGroupModalOpen(true);
    dispatch(getAvailableGuestGroupsByEvent(eventId));
    trackDemoUserAction("open_claim_modal", "claim_group_modal");
  }, [
    shouldShowClaimModal,
    eventId,
    isClaimGroupModalOpen,
    dispatch,
    trackDemoUserAction
  ]);

  useEffect(() => {
    if (claimGuestGroupSubmitSuccess) {
      setIsClaimGroupModalOpen(false);
      trackDemoUserAction("claim_submit_success", "claim_group_modal");
    }
  }, [claimGuestGroupSubmitSuccess, trackDemoUserAction]);

  useEffect(() => {
    if (claimGuestGroupSubmitError) {
      trackDemoUserAction("claim_submit_error", "claim_group_modal");
    }
  }, [claimGuestGroupSubmitError, trackDemoUserAction]);

  useEffect(() => {
    const handleScroll = () => {
      if (hasTrackedPageEnd.current) {
        return;
      }

      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 40
      ) {
        hasTrackedPageEnd.current = true;
        latestTrackUserAction.current("page_reached_end");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const hotelName = getHotelName(deviceDetails);
  const guestFirstName = deviceDetails?.attendance?.guestFirstName;
  const guestLastName = deviceDetails?.attendance?.guestLastName;
  const guestName = [guestFirstName, guestLastName].filter(Boolean).join(" ");
  const groupName = deviceDetails?.group?.name;
  const scheduleDays = deviceDetails?.schedules?.[0]?.days || [];

  const openOverlay = (name) => {
    trackDemoButtonClick(`open_${name}`, "discover_section");
    if (name === "balance") setIsBalanceOpen(true);
    if (name === "memories") setIsMemoriesOpen(true);
    if (name === "pass") setIsPassOpen(true);
  };

  const closeOverlay = (name) => {
    trackDemoButtonClick(`close_${name}`, `${name}_modal`);
    if (name === "balance") setIsBalanceOpen(false);
    if (name === "memories") setIsMemoriesOpen(false);
    if (name === "pass") setIsPassOpen(false);
  };

  const waitingForDetails =
    hasRequiredParams &&
    (gettingDeviceDetails || (!deviceDetails && !errorGettingDeviceDetails));

  if (waitingForDetails) {
    return (
      <MainContainer>
        <NavBar>
          <div className="wordmark">
            <span className="brand">Northbank</span>
            <span className="event">Riverside Sessions</span>
          </div>
        </NavBar>
        <PageStatus role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <p>Loading your event</p>
        </PageStatus>
      </MainContainer>
    );
  }

  const detailsFailed =
    hasRequiredParams && Boolean(errorGettingDeviceDetails) && !deviceDetails;

  return (
    <MainContainer>
      <NavBar>
        <div className="wordmark">
          <span className="brand">Northbank</span>
          <span className="event">Riverside Sessions</span>
        </div>
      </NavBar>

      {!hasRequiredParams && (
        <MissingParams>
          <h2>Add event details</h2>
          <p>
            This page needs a band serial and an event id in the URL so it can
            load your guest, group, schedule and hotel.
          </p>
        </MissingParams>
      )}

      {detailsFailed && (
        <MissingParams>
          <h2>Could not load this event</h2>
          <p>
            Check the serial and event id in the URL, then refresh the page.
          </p>
        </MissingParams>
      )}

      {hasRequiredParams && !detailsFailed && (
        <>
          <Hero>
            <img src={IMAGES.hero} alt="" />
            <div className="overlay">
              {guestFirstName ? (
                <h1>
                  Welcome,
                  <br />
                  {guestName}
                </h1>
              ) : (
                <h1>Welcome to Riverside Sessions</h1>
              )}
              <div className="chips">
                {groupName && <Chip $tone="gold">{groupName}</Chip>}
                {hotelName && <Chip>{hotelName}</Chip>}
              </div>
            </div>
          </Hero>

          <Section>
            <div className="section-head">
              <p className="eyebrow">Explore</p>
              <h2>Your event tools</h2>
            </div>
            <TileGrid>
              <Tile
                type="button"
                onClick={() => openOverlay("balance")}
              >
                <img src={IMAGES.spend} alt="" />
                <div className="tile-copy">
                  <h3>Harbor Pay</h3>
                  <p>View spend on your band</p>
                </div>
              </Tile>
              <Tile
                type="button"
                onClick={() => openOverlay("memories")}
              >
                <img src={IMAGES.stage} alt="" />
                <div className="tile-copy">
                  <h3>Lumen</h3>
                  <p>Open your photo collection</p>
                </div>
              </Tile>
            </TileGrid>
          </Section>

          <Section className="teal" id="schedule">
            <div className="section-head">
              <p className="eyebrow">Programme</p>
              <h2>Your schedule</h2>
            </div>
            <p className="muted">
              Times are local to the event. Transfers leave promptly from the
              hotel lobby.
            </p>
            <ProgramSchedule
              days={scheduleDays}
              error={errorGettingDeviceDetails}
            />
          </Section>

          <Section className="sand" id="stay">
            <div className="section-head">
              <p className="eyebrow">Your stay</p>
              <h2>Hotel & pass</h2>
            </div>
            <HotelCard
              hotelName={hotelName}
              onAddPass={() => openOverlay("pass")}
            />
            {!hotelName && (
              <p className="muted">
                Hotel details will appear here once they are assigned to your
                group.
              </p>
            )}
          </Section>

          <Section>
            <div className="section-head">
              <p className="eyebrow">About</p>
              <h2>Riverside Sessions</h2>
            </div>
            <p>
              A two-day gathering on the water, hosted by Northbank. Live
              sessions, shared meals, and a simple guest band for access, spend
              and your daily programme.
            </p>
            <p className="muted">
              This page is a product demo of the events platform. Names, hotels
              and vendors on this page are fictional.
            </p>
          </Section>
        </>
      )}

      <Footer>
        <p className="bold">Riverside Sessions</p>
        <p>Hosted by Northbank</p>
        <p className="legal">Privacy policy</p>
        <p className="legal">Terms & conditions</p>
        {serialNumber && (
          <div className="copy-row">
            <p>ID: {serialNumber}</p>
            <button
              className="copy-button"
              type="button"
              aria-label="Copy band ID"
              onClick={() => {
                trackDemoButtonClick("copy_serial_number", "footer");
                navigator.clipboard.writeText(serialNumber);
                setCopied(true);
              }}
            >
              <img src={copyIconLight} alt="" />
            </button>
          </div>
        )}
        {copied && <p>Copied to clipboard</p>}
        <p className="version">v.{packageJson.version}</p>
      </Footer>

      {isClaimGroupModalOpen && shouldShowClaimModal && (
        <ClaimGroupModal
          groups={availableGuestGroups}
          initialValues={{
            guestFirstName: deviceDetails?.attendance?.guestFirstName,
            guestLastName: deviceDetails?.attendance?.guestLastName,
            email: deviceDetails?.attendance?.email
          }}
          loadingGroups={gettingAvailableGuestGroups}
          loadingGroupsError={errorGettingAvailableGuestGroups}
          submitting={claimGuestGroupSubmitting}
          submitError={claimGuestGroupSubmitError}
          onSubmit={(payload) => {
            dispatch(
              submitGuestGroupClaimAction({
                eventId,
                serialNumber,
                locale: "en",
                ...payload
              })
            );
          }}
          onTrackButtonClick={trackDemoButtonClick}
        />
      )}
      {isBalanceOpen && (
        <Balance
          onClose={() => closeOverlay("balance")}
          onTrackButtonClick={trackDemoButtonClick}
        />
      )}
      {isMemoriesOpen && (
        <Memories
          onClose={() => closeOverlay("memories")}
          onTrackButtonClick={trackDemoButtonClick}
        />
      )}
      {isPassOpen && (
        <PassPreview
          guestName={guestName}
          groupName={groupName}
          hotelName={hotelName}
          serialNumber={serialNumber}
          onClose={() => closeOverlay("pass")}
        />
      )}
    </MainContainer>
  );
};

export default DemoEvent;
